import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  AreaChart, BarChart, DonutChart, FunnelChart, MetricCard,
} from 'foundry-charts';
import 'foundry-charts/styles';
import clsx from 'clsx';

// ── Formatting helpers ──────────────────────────────────────────────────────

function fmt$(v) {
  const n = Number(v || 0);
  if (n >= 1_000_000) return '$' + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(n >= 100_000 ? 0 : 1) + 'k';
  return '$' + Math.round(n).toLocaleString();
}

function fmtMonth({ year, month }) {
  return new Date(year, month - 1, 1)
    .toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function truncate(s, n = 22) {
  return s && s.length > n ? s.slice(0, n - 1) + '…' : s;
}

function pct(curr, prev) {
  const a = Number(curr || 0), b = Number(prev || 0);
  if (b === 0) return undefined;
  return (a - b) / b;
}

function fmtPct(v) {
  if (v === undefined || v === null) return '—';
  const sign = v >= 0 ? '+' : '';
  return sign + (v * 100).toFixed(1) + '%';
}

// ── Constants ───────────────────────────────────────────────────────────────

const NAVY    = '#0A2240';
const CRIMSON = '#C8102E';
const GOLD    = '#D4A017';
const BLUE2   = '#2563EB';
const TEAL    = '#0D9488';
const SLATE   = '#64748B';

const STAGE_LABELS = {
  lead:        'Lead',
  prospect:    'Prospect',
  qualified:   'Qualified',
  quoted:      'Quoted',
  negotiating: 'Negotiating',
  won:         'Won',
  lost:        'Lost',
};

const STATUS_COLORS = {
  Available: TEAL,
  Pending:   GOLD,
  Sold:      NAVY,
  Hold:      SLATE,
  'In Shop': BLUE2,
};

const TOUCH_COLORS = {
  visit: NAVY,
  call:  BLUE2,
  email: TEAL,
  quote: CRIMSON,
  note:  SLATE,
};

// ── Shared UI pieces ────────────────────────────────────────────────────────

function SectionCard({ title, subtitle, children, className }) {
  return (
    <div className={clsx('bg-white border border-navy-100 rounded-lg p-4 shadow-sm', className)}>
      {title && (
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-navy">{title}</h3>
          {subtitle && <p className="text-[11px] text-muted mt-0.5">{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

function LoadingBlock({ rows = 4 }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-5 bg-navy-100 rounded" style={{ opacity: 1 - i * 0.2 }} />
      ))}
    </div>
  );
}

function EmptyState({ message = 'No data for this period.' }) {
  return <p className="text-sm text-muted py-6 text-center">{message}</p>;
}

function TrendBadge({ curr, prev }) {
  const v = pct(curr, prev);
  if (v === undefined) return null;
  return (
    <span className={clsx(
      'text-xs px-1.5 py-0.5 rounded font-medium',
      v >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
    )}>
      {fmtPct(v)} vs prev
    </span>
  );
}

// ── Overview tab ────────────────────────────────────────────────────────────

const PERIOD_LABELS = { '30d': '30 Days', '90d': '90 Days', 'ytd': 'YTD', '12m': '12 Months' };

function OverviewTab({ period }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'overview', period],
    queryFn: () => api.get(`/reports/overview?period=${period}`).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError)   return <p className="text-crimson text-sm">Failed to load overview.</p>;

  const kpi = data.kpi;
  const kpiPeriod = kpi.kpi_period;
  const nowForLabel = new Date();
  const isCurrentMonth = kpiPeriod &&
    kpiPeriod.year  === nowForLabel.getFullYear() &&
    kpiPeriod.month === nowForLabel.getMonth() + 1;
  const periodSuffix = kpiPeriod && !isCurrentMonth
    ? ` — ${fmtMonth(kpiPeriod)}` : '';

  const trendData = data.revenue_trend.map(r => ({
    month:    fmtMonth(r),
    Parts:    Number(r.parts_sales   || 0),
    Trailer:  Number(r.trailer_sales || 0),
    Service:  Number(r.service_sales || 0),
  }));

  const stageData = data.pipeline.by_stage.map(r => ({
    stage: STAGE_LABELS[r.stage] || r.stage,
    Value: Number(r.value || 0),
  }));

  const partsTrend  = pct(kpi.parts_this_month, kpi.parts_last_month);
  const trailerTrend = pct(kpi.trailer_this_month, kpi.trailer_last_month);

  return (
    <div className="space-y-4">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SectionCard>
          <MetricCard
            label={`Parts MTD${periodSuffix}`}
            value={Number(kpi.parts_this_month || 0)}
            formatValue={fmt$}
            trend={partsTrend}
            trendLabel="vs last month"
          />
        </SectionCard>
        <SectionCard>
          <MetricCard
            label={`Trailer MTD${periodSuffix}`}
            value={Number(kpi.trailer_this_month || 0)}
            formatValue={fmt$}
            trend={trailerTrend}
            trendLabel="vs last month"
          />
        </SectionCard>
        <SectionCard>
          <MetricCard
            label="Open Pipeline"
            value={Number(kpi.open_pipeline || 0)}
            formatValue={fmt$}
          />
        </SectionCard>
        <SectionCard>
          <MetricCard
            label="Weighted Pipeline"
            value={Number(kpi.weighted_pipeline || 0)}
            formatValue={fmt$}
          />
        </SectionCard>
      </div>

      {/* Revenue trend */}
      <SectionCard title={`Revenue Trend — ${PERIOD_LABELS[period] || period}`}>
        {trendData.length === 0 ? <EmptyState /> : (
          <AreaChart
            data={trendData}
            xKey="month"
            series={[
              { key: 'Parts',   label: 'Parts',   color: NAVY    },
              { key: 'Trailer', label: 'Trailer', color: CRIMSON },
              { key: 'Service', label: 'Service', color: GOLD    },
            ]}
            variant="stacked"
            height={260}
            formatY={fmt$}
          />
        )}
      </SectionCard>

      {/* Pipeline by stage */}
      <SectionCard title="Open Pipeline by Stage" subtitle="Current open opportunities — not filtered by the period above">
        {stageData.length === 0 ? <EmptyState message="No open opportunities." /> : (
          <BarChart
            data={stageData}
            xKey="stage"
            series={[{ key: 'Value', label: 'Pipeline Value', color: CRIMSON }]}
            height={200}
            formatY={fmt$}
          />
        )}
      </SectionCard>
    </div>
  );
}

// ── Parts tab ───────────────────────────────────────────────────────────────

function PartsTab({ period }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'parts', period],
    queryFn: () => api.get(`/reports/parts?period=${period}`).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError)   return <p className="text-crimson text-sm">Failed to load parts data.</p>;

  const trendData = data.trend.map(r => ({
    month:  fmtMonth(r),
    Parts:  Number(r.parts_sales || 0),
  }));

  const topData = data.top_customers.map(r => ({
    name:  truncate(r.name),
    Sales: Number(r.total || 0),
  }));

  const branchData = data.by_branch.map(r => ({
    label: r.branch,
    value: Number(r.total || 0),
  }));

  const branchTotal = branchData.reduce((s, r) => s + r.value, 0);

  return (
    <div className="space-y-4">
      {/* Period KPI */}
      <SectionCard>
        <div className="flex items-baseline gap-3">
          <span className="text-2xl font-bold text-navy">{fmt$(data.period_total)}</span>
          <span className="text-sm text-muted">parts sales in period</span>
        </div>
      </SectionCard>

      {/* Monthly trend */}
      <SectionCard title="Monthly Parts Sales">
        {trendData.length === 0 ? <EmptyState /> : (
          <AreaChart
            data={trendData}
            xKey="month"
            series={[{ key: 'Parts', label: 'Parts Sales', color: NAVY }]}
            height={220}
            formatY={fmt$}
          />
        )}
      </SectionCard>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Top customers */}
        <SectionCard title="Top Customers by Parts">
          {topData.length === 0 ? <EmptyState /> : (
            <BarChart
              data={topData}
              xKey="name"
              series={[{ key: 'Sales', label: 'Parts Sales', color: NAVY }]}
              height={280}
              formatY={fmt$}
            />
          )}
        </SectionCard>

        {/* By branch */}
        <SectionCard title="Sales by Branch">
          {branchData.length === 0 ? <EmptyState /> : (
            <>
              <DonutChart
                data={branchData}
                formatValue={fmt$}
                centerLabel={fmt$(branchTotal)}
                height={220}
              />
              <div className="mt-3 space-y-1">
                {branchData.map(r => (
                  <div key={r.label} className="flex justify-between text-xs">
                    <span className="text-muted">{r.label}</span>
                    <span className="font-medium text-navy">{fmt$(r.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

// ── Trailer Sales tab ────────────────────────────────────────────────────────

function TrailerTab({ period }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'trailer', period],
    queryFn: () => api.get(`/reports/trailer?period=${period}`).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError)   return <p className="text-crimson text-sm">Failed to load trailer data.</p>;

  const funnelData = data.pipeline.map(r => ({
    label: STAGE_LABELS[r.stage] || r.stage,
    value: Number(r.value || 0),
  }));

  const inventoryData = data.inventory.map(r => ({
    label: r.status,
    value: r.count,
    color: STATUS_COLORS[r.status] || SLATE,
  }));

  const trendData = data.trend.map(r => ({
    month:   fmtMonth(r),
    Trailer: Number(r.trailer_sales || 0),
  }));

  const pipelineValue = data.pipeline.reduce((s, r) => s + Number(r.value || 0), 0);
  const pipelineCount = data.pipeline.reduce((s, r) => s + Number(r.count || 0), 0);
  const inventoryTotal = data.inventory.reduce((s, r) => s + r.count, 0);
  const availableCount = data.inventory.find(r => r.status === 'Available')?.count || 0;

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <SectionCard>
          <MetricCard label="Pipeline Value" value={pipelineValue} formatValue={fmt$} />
        </SectionCard>
        <SectionCard>
          <MetricCard label="Open Deals" value={pipelineCount} formatValue={v => v} />
        </SectionCard>
        <SectionCard>
          <MetricCard label="Available Units" value={availableCount} formatValue={v => `${v} of ${inventoryTotal}`} />
        </SectionCard>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Pipeline funnel */}
        <SectionCard title="Pipeline by Stage">
          {funnelData.length === 0 ? <EmptyState message="No open opportunities." /> : (
            <FunnelChart
              data={funnelData}
              formatValue={fmt$}
              showConversion
              height={260}
            />
          )}
        </SectionCard>

        {/* Inventory status */}
        <SectionCard title="Fresno Inventory Status">
          {inventoryData.length === 0 ? <EmptyState message="No inventory records." /> : (
            <>
              <DonutChart
                data={inventoryData}
                formatValue={v => `${v} units`}
                centerLabel={`${inventoryTotal} total`}
                height={220}
              />
              <div className="mt-3 grid grid-cols-2 gap-1">
                {inventoryData.map(r => (
                  <div key={r.label} className="flex items-center gap-2 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ background: r.color }} />
                    <span className="text-muted">{r.label}</span>
                    <span className="ml-auto font-medium text-navy">{r.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </SectionCard>
      </div>

      {/* Revenue trend */}
      <SectionCard title="Trailer Revenue Trend">
        {trendData.length === 0 ? <EmptyState /> : (
          <AreaChart
            data={trendData}
            xKey="month"
            series={[{ key: 'Trailer', label: 'Trailer Sales', color: CRIMSON }]}
            height={200}
            formatY={fmt$}
          />
        )}
      </SectionCard>

      {/* Top customers */}
      {data.top_customers.length > 0 && (
        <SectionCard title="Top Trailer Customers">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-100">
                  <th className="text-left py-1.5 pr-4 text-muted font-medium">Customer</th>
                  <th className="text-right py-1.5 text-muted font-medium">Sales</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {data.top_customers.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5 pr-4 text-navy">{r.name}</td>
                    <td className="py-1.5 text-right font-medium">{fmt$(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Team Activity tab ────────────────────────────────────────────────────────

function ActivityTab({ period }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports', 'activity', period],
    queryFn: () => api.get(`/reports/activity?period=${period}`).then(r => r.data),
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError)   return <p className="text-crimson text-sm">Failed to load activity data.</p>;

  const touchData = data.touches.map(r => ({
    rep:   truncate(r.rep, 18),
    Visit: r.visit  || 0,
    Call:  r.call   || 0,
    Email: r.email  || 0,
    Quote: r.quote  || 0,
    Note:  r.note   || 0,
  }));

  const stageData = data.opps_by_stage.map(r => ({
    stage: STAGE_LABELS[r.stage] || r.stage,
    Value: Number(r.value || 0),
  }));

  return (
    <div className="space-y-4">
      {/* Touches by rep */}
      <SectionCard title="Touches by Rep">
        {touchData.length === 0 ? <EmptyState message="No activity in this period." /> : (
          <BarChart
            data={touchData}
            xKey="rep"
            variant="stacked"
            series={[
              { key: 'Visit', label: 'Visit',  color: TOUCH_COLORS.visit  },
              { key: 'Call',  label: 'Call',   color: TOUCH_COLORS.call   },
              { key: 'Email', label: 'Email',  color: TOUCH_COLORS.email  },
              { key: 'Quote', label: 'Quote',  color: TOUCH_COLORS.quote  },
              { key: 'Note',  label: 'Note',   color: TOUCH_COLORS.note   },
            ]}
            height={260}
            formatY={v => `${v}`}
          />
        )}
      </SectionCard>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Follow-up completion table */}
        <SectionCard title="Follow-up Completion by Rep">
          {data.followups.length === 0 ? <EmptyState /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-navy-100 text-muted font-medium">
                    <th className="text-left py-1.5 pr-3">Rep</th>
                    <th className="text-right py-1.5 px-2">Done</th>
                    <th className="text-right py-1.5 px-2">Missed</th>
                    <th className="text-right py-1.5 pl-2">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-100">
                  {data.followups.map((r, i) => {
                    const rate = r.total > 0
                      ? Math.round((r.done / r.total) * 100)
                      : 0;
                    return (
                      <tr key={i}>
                        <td className="py-1.5 pr-3 text-navy">{truncate(r.rep, 20)}</td>
                        <td className="py-1.5 px-2 text-right text-green-700 font-medium">{r.done}</td>
                        <td className="py-1.5 px-2 text-right text-crimson font-medium">{r.missed}</td>
                        <td className="py-1.5 pl-2 text-right">
                          <span className={clsx(
                            'text-xs font-semibold px-1.5 py-0.5 rounded',
                            rate >= 80 ? 'bg-green-50 text-green-700'
                              : rate >= 60 ? 'bg-yellow-50 text-yellow-700'
                              : 'bg-red-50 text-red-600'
                          )}>
                            {rate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        {/* Opportunities by stage */}
        <SectionCard title="Open Opportunities by Stage">
          {stageData.length === 0 ? <EmptyState message="No open opportunities." /> : (
            <BarChart
              data={stageData}
              xKey="stage"
              series={[{ key: 'Value', label: 'Pipeline Value', color: NAVY }]}
              height={220}
              formatY={fmt$}
            />
          )}
        </SectionCard>
      </div>

      {/* Won deals in period */}
      {data.won.length > 0 && (
        <SectionCard title="Won Deals in Period">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-navy-100 text-muted font-medium">
                  <th className="text-left py-1.5 pr-4">Rep</th>
                  <th className="text-right py-1.5 px-2">Deals Won</th>
                  <th className="text-right py-1.5 pl-2">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {data.won.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5 pr-4 text-navy">{r.rep}</td>
                    <td className="py-1.5 px-2 text-right font-medium">{r.won_count}</td>
                    <td className="py-1.5 pl-2 text-right font-semibold text-navy">{fmt$(r.won_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Shared result table ──────────────────────────────────────────────────────

function ResultTable({ title, description, columns, rows }) {
  return (
    <SectionCard title={title || 'Results'}>
      {description && <p className="text-sm text-muted mb-3">{description}</p>}
      {rows.length === 0 ? (
        <EmptyState message="No rows returned for this query." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-navy-100">
                {columns.map(col => (
                  <th key={col} className="text-left py-1.5 pr-4 text-xs text-muted font-semibold uppercase tracking-wider whitespace-nowrap">
                    {col.replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-panel/50">
                  {columns.map(col => {
                    const v = row[col];
                    const num = typeof v === 'number' || (!isNaN(Number(v)) && v !== '' && v !== null);
                    return (
                      <td key={col} className={clsx('py-1.5 pr-4 tabular-nums', num ? 'text-right font-medium' : 'text-navy')}>
                        {v === null || v === undefined ? <span className="text-muted">—</span> : String(v)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-[10px] text-muted mt-2">{rows.length} row{rows.length !== 1 ? 's' : ''} · capped at 200</p>
        </div>
      )}
    </SectionCard>
  );
}

// ── Saved report runner ──────────────────────────────────────────────────────

function SavedReportRunner({ report, onDelete, onResult }) {
  const params = Array.isArray(report.parameters) ? report.parameters : [];
  const [values, setValues] = useState(() =>
    Object.fromEntries(params.map(p => [p.name, p.default_value ?? '']))
  );
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);
  const [showParams, setShowParams] = useState(false);

  async function run() {
    setRunning(true); setErr(null);
    try {
      const resp = await api.post(`/reports/saved/${report.id}/run`, { parameter_values: values });
      if (resp.data.success) {
        onResult(resp.data);
      } else {
        setErr(resp.data.error || 'Run failed');
      }
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setRunning(false);
    }
  }

  async function handleRun() {
    if (params.length > 0 && !showParams) { setShowParams(true); return; }
    await run();
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${report.title}"?`)) return;
    try {
      await api.delete(`/reports/saved/${report.id}`);
      onDelete(report.id);
    } catch (e) {
      alert(e?.response?.data?.error || 'Delete failed');
    }
  }

  return (
    <div className="rounded-lg border border-navy-100 px-3 py-2.5">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-navy truncate">{report.title}</p>
          {report.description && <p className="text-xs text-muted mt-0.5 line-clamp-1">{report.description}</p>}
          {params.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {params.map(p => (
                <span key={p.name} className="text-[10px] px-1.5 py-0.5 bg-panel rounded-full text-muted border border-navy-100">
                  {p.label}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={handleRun}
            disabled={running}
            className="text-xs px-2.5 py-1 rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50 transition-colors"
          >
            {running ? '…' : params.length > 0 && !showParams ? 'Set params' : 'Run'}
          </button>
          <button
            onClick={handleDelete}
            className="text-xs px-2 py-1 rounded text-muted hover:text-crimson hover:bg-red-50 transition-colors"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Parameter form */}
      {showParams && params.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-navy-100 space-y-2">
          {params.map(p => (
            <div key={p.name} className="flex items-center gap-2">
              <label className="text-xs text-muted w-24 shrink-0">{p.label}</label>
              {p.type === 'date' ? (
                <input type="date" value={values[p.name] || ''} onChange={e => setValues(v => ({...v, [p.name]: e.target.value}))}
                  className="flex-1 text-xs border border-navy-100 rounded px-2 py-1 text-navy" />
              ) : p.type === 'number' ? (
                <input type="number" value={values[p.name] || ''} onChange={e => setValues(v => ({...v, [p.name]: e.target.value}))}
                  className="flex-1 text-xs border border-navy-100 rounded px-2 py-1 text-navy" />
              ) : (
                <input type="text" value={values[p.name] || ''} onChange={e => setValues(v => ({...v, [p.name]: e.target.value}))}
                  className="flex-1 text-xs border border-navy-100 rounded px-2 py-1 text-navy" />
              )}
            </div>
          ))}
          <button onClick={run} disabled={running}
            className="text-xs px-3 py-1 rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50 transition-colors">
            {running ? 'Running…' : 'Run ▶'}
          </button>
        </div>
      )}

      {err && <p className="text-xs text-crimson mt-1.5">{err}</p>}
    </div>
  );
}

// ── Report Builder (AI) tab ──────────────────────────────────────────────────

function BuildTab() {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult]  = useState(null);
  const [error, setError]    = useState(null);

  // Save flow
  const [showSave, setShowSave] = useState(false);
  const [saveTitle, setSaveTitle] = useState('');
  const [saveDesc, setSaveDesc]   = useState('');
  const [saveSql, setSaveSql]     = useState('');
  const [saveParams, setSaveParams] = useState([]);
  const [analyzing, setAnalyzing]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [saveErr, setSaveErr]       = useState(null);

  // Saved reports
  const [savedReports, setSavedReports] = useState(null);
  const [savedLoading, setSavedLoading] = useState(false);
  const [runResult, setRunResult]       = useState(null);

  // Load saved reports on mount
  useEffect(() => {
    setSavedLoading(true);
    api.get('/reports/saved')
      .then(r => setSavedReports(r.data.reports || []))
      .catch(() => setSavedReports([]))
      .finally(() => setSavedLoading(false));
  }, []);

  async function handleBuild(e) {
    e.preventDefault();
    if (!prompt.trim()) return;
    setLoading(true); setError(null); setResult(null); setShowSave(false); setRunResult(null);
    try {
      const resp = await api.post('/reports/build', { prompt: prompt.trim() });
      if (resp.data.success) setResult(resp.data);
      else setError(resp.data.error || 'Unknown error');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  async function openSaveForm() {
    if (!result) return;
    setSaveTitle(result.title || '');
    setSaveDesc(result.description || '');
    setSaveSql(result.sql || '');
    setSaveParams([]);
    setSaveErr(null);
    setShowSave(true);

    // Detect parameters in background
    if (result.sql) {
      setAnalyzing(true);
      try {
        const resp = await api.post('/reports/analyze-params', { sql: result.sql });
        if (resp.data.success && resp.data.has_parameters) {
          setSaveSql(resp.data.suggested_sql || result.sql);
          setSaveParams(resp.data.parameters || []);
        }
      } catch { /* ignore */ } finally {
        setAnalyzing(false);
      }
    }
  }

  function removeParam(idx) {
    setSaveParams(ps => ps.filter((_, i) => i !== idx));
  }

  async function handleSave() {
    if (!saveTitle.trim()) { setSaveErr('Title required'); return; }
    setSaving(true); setSaveErr(null);
    try {
      const resp = await api.post('/reports/saved', {
        title: saveTitle.trim(),
        description: saveDesc.trim() || null,
        sql: saveSql,
        parameters: saveParams,
      });
      if (resp.data.success) {
        // Reload saved list
        const list = await api.get('/reports/saved');
        setSavedReports(list.data.reports || []);
        setShowSave(false);
      } else {
        setSaveErr(resp.data.error || 'Save failed');
      }
    } catch (e) {
      setSaveErr(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }

  function handleDeleteSaved(id) {
    setSavedReports(rs => rs.filter(r => r.id !== id));
  }

  const suggestions = [
    'Top 10 customers by parts sales this year',
    'Follow-up completion rate by rep last 90 days',
    'Open opportunities by stage with rep name',
    'Parts sales by branch last 30 days',
    'Customers with no activity notes in 60 days',
    'Won deals this year by rep with total value',
  ];

  return (
    <div className="space-y-4">
      {/* Builder */}
      <SectionCard title="AI Report Builder">
        <p className="text-sm text-muted mb-3">
          Describe the report you want in plain English. The AI will generate a SQL query, run it against live data, and show you the results.
        </p>
        <form onSubmit={handleBuild} className="space-y-3">
          <textarea
            className="w-full rounded-lg border border-navy-100 bg-panel px-3 py-2 text-sm text-navy resize-none focus:outline-none focus:ring-2 focus:ring-navy/20"
            rows={3}
            placeholder="e.g. Show me parts sales by branch for the last 90 days"
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            disabled={loading}
          />
          <div className="flex items-center gap-2">
            <button type="submit" disabled={loading || !prompt.trim()}
              className="px-4 py-1.5 rounded bg-navy text-white text-sm font-medium hover:bg-navy/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {loading ? 'Generating…' : 'Build Report'}
            </button>
            {prompt && !loading && (
              <button type="button" className="text-xs text-muted hover:text-navy"
                onClick={() => { setPrompt(''); setResult(null); setError(null); setShowSave(false); }}>
                Clear
              </button>
            )}
          </div>
        </form>

        {!result && !loading && (
          <div className="mt-4">
            <p className="text-xs text-muted mb-2 font-medium uppercase tracking-wider">Try these</p>
            <div className="flex flex-wrap gap-2">
              {suggestions.map(s => (
                <button key={s} onClick={() => setPrompt(s)}
                  className="text-xs px-2.5 py-1 rounded-full border border-navy-100 text-navy hover:bg-panel transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {error && <SectionCard><p className="text-sm text-crimson">{error}</p></SectionCard>}

      {/* New report result */}
      {result && !showSave && (
        <>
          <ResultTable title={result.title} description={result.description} columns={result.columns} rows={result.rows} />
          <div className="flex justify-end">
            <button onClick={openSaveForm}
              className="text-sm px-4 py-1.5 rounded border border-navy text-navy hover:bg-panel transition-colors font-medium">
              ✦ Save this report
            </button>
          </div>
        </>
      )}

      {/* Save form */}
      {showSave && (
        <SectionCard title="Save Report">
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted block mb-1">Title</label>
              <input type="text" value={saveTitle} onChange={e => setSaveTitle(e.target.value)}
                className="w-full text-sm border border-navy-100 rounded-lg px-3 py-1.5 text-navy focus:outline-none focus:ring-2 focus:ring-navy/20" />
            </div>
            <div>
              <label className="text-xs text-muted block mb-1">Description (optional)</label>
              <input type="text" value={saveDesc} onChange={e => setSaveDesc(e.target.value)}
                className="w-full text-sm border border-navy-100 rounded-lg px-3 py-1.5 text-navy focus:outline-none focus:ring-2 focus:ring-navy/20" />
            </div>

            {/* Parameters */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-xs text-muted font-medium uppercase tracking-wider">Parameters</p>
                {analyzing && <span className="text-[10px] text-muted animate-pulse">Detecting…</span>}
              </div>
              {saveParams.length === 0 && !analyzing && (
                <p className="text-xs text-muted">No parameters detected — this report will always run the same query.</p>
              )}
              {saveParams.length > 0 && (
                <div className="space-y-1.5">
                  {saveParams.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-panel rounded-lg px-3 py-2">
                      <span className="font-mono text-navy bg-white border border-navy-100 px-1.5 py-0.5 rounded text-[10px]">{p.token}</span>
                      <span className="text-muted">→</span>
                      <input type="text" value={p.label}
                        onChange={e => setSaveParams(ps => ps.map((pp, ii) => ii === i ? {...pp, label: e.target.value} : pp))}
                        className="flex-1 border border-navy-100 rounded px-2 py-0.5 text-navy" />
                      <span className="text-muted text-[10px] w-10 shrink-0">{p.type}</span>
                      <input type="text" value={p.default_value ?? ''}
                        onChange={e => setSaveParams(ps => ps.map((pp, ii) => ii === i ? {...pp, default_value: e.target.value} : pp))}
                        placeholder="default"
                        className="w-24 border border-navy-100 rounded px-2 py-0.5 text-muted" />
                      <button onClick={() => removeParam(i)} className="text-muted hover:text-crimson">✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {saveErr && <p className="text-xs text-crimson">{saveErr}</p>}

            <div className="flex items-center gap-2 pt-1">
              <button onClick={handleSave} disabled={saving || analyzing}
                className="px-4 py-1.5 rounded bg-navy text-white text-sm font-medium hover:bg-navy/90 disabled:opacity-50 transition-colors">
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setShowSave(false)} className="text-xs text-muted hover:text-navy">Cancel</button>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Saved report run result */}
      {runResult && (
        <ResultTable title={runResult.title} description={runResult.description}
          columns={runResult.columns} rows={runResult.rows} />
      )}

      {/* Saved reports panel */}
      {(savedReports && savedReports.length > 0) && (
        <SectionCard title="Saved Reports">
          {savedLoading ? <LoadingBlock rows={2} /> : (
            <div className="space-y-2">
              {savedReports.map(r => (
                <SavedReportRunner key={r.id} report={r}
                  onDelete={handleDeleteSaved}
                  onResult={data => { setRunResult(data); setResult(null); setShowSave(false); }} />
              ))}
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
}

// ── Main Reports page ────────────────────────────────────────────────────────

const PERIODS = [
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
  { key: 'ytd', label: 'YTD'     },
  { key: '12m', label: '12 mo'   },
];

const TABS = [
  { key: 'mtd',      label: 'MTD'           },
  { key: 'overview', label: 'Overview'      },
  { key: 'parts',    label: 'Parts Sales'   },
  { key: 'trailer',  label: 'Trailer Sales' },
  { key: 'activity', label: 'Team Activity' },
  { key: 'build',    label: '✦ Build'       },
];

export default function Reports() {
  const { user } = useAuth();
  // The Build (free-SQL) tab is admin-only — it can read dealership-wide; hide it
  // from 'sales' so they don't hit a 403. Server enforces this regardless.
  const canBuild = ['superadmin', 'dealer_admin', 'branch_admin'].includes(user?.role);
  const tabs = canBuild ? TABS : TABS.filter(t => t.key !== 'build');
  const [tab, setTab]     = useState('overview');
  const [period, setPeriod] = useState('90d');

  return (
    <div className="space-y-4">
      <header>
        <div className="section-eyebrow">Reporting</div>
        <h1 className="text-2xl">Sales Overview</h1>
        <div className="rule-crimson" />
      </header>

      {/* Controls bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Tab bar */}
        <div className="flex gap-1 bg-panel rounded-lg p-1">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={clsx(
                'px-3 py-1.5 rounded text-sm font-medium transition-colors',
                tab === t.key
                  ? 'bg-white text-navy shadow-sm'
                  : 'text-slate-600 hover:text-navy'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Period picker — hidden on MTD and Build tabs */}
        {tab !== 'mtd' && tab !== 'build' && (
          <div className="flex gap-1 bg-panel rounded-lg p-1">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={clsx(
                  'px-3 py-1.5 rounded text-sm transition-colors',
                  period === p.key
                    ? 'bg-white text-navy font-medium shadow-sm'
                    : 'text-slate-600 hover:text-navy'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tab content */}
      {tab === 'mtd'      && <MtdTab />}
      {tab === 'overview' && <OverviewTab period={period} />}
      {tab === 'parts'    && <PartsTab    period={period} />}
      {tab === 'trailer'  && <TrailerTab  period={period} />}
      {tab === 'activity' && <ActivityTab period={period} />}
      {tab === 'build'    && <BuildTab />}
    </div>
  );
}

// ── MTD tab ─────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function MtdTab() {
  const q = useQuery({
    queryKey: ['reports', 'mtd'],
    queryFn: () => api.get('/reports/mtd').then(r => r.data),
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) return <div className="text-muted">Loading…</div>;
  if (q.isError)   return <div className="text-crimson">Failed to load.</div>;

  const d = q.data;
  const monthLabel = `${MONTH_NAMES[(d.period.month - 1)]} ${d.period.year}`;

  function pctChg(curr, prev) {
    if (!prev || prev === 0) return null;
    return (curr - prev) / prev;
  }

  function MtdCard({ label, curr, prev, prevLabel }) {
    const chg = pctChg(curr, prev);
    return (
      <div className="card p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted">{label}</div>
        <div className="text-2xl font-bold text-navy tabular-nums">{fmt$(curr)}</div>
        {prev > 0 && (
          <div className={`text-xs mt-0.5 ${chg >= 0 ? 'text-emerald-700' : 'text-crimson'}`}>
            {chg >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(chg))} vs {prevLabel}
          </div>
        )}
      </div>
    );
  }

  const totalMtd  = d.mtd.parts + d.mtd.trailer + d.mtd.service;
  const totalLast = d.last_month.parts + d.last_month.trailer + d.last_month.service;
  const totalPrYr = d.prior_year_month.parts + d.prior_year_month.trailer + d.prior_year_month.service;
  const totalYtd  = d.ytd.parts + d.ytd.trailer + d.ytd.service;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-navy uppercase tracking-wide">{monthLabel} — Month to Date</h2>
        <span className="text-xs text-muted">vs prior month · vs same month last year</span>
      </div>

      {/* Revenue KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MtdCard label="Total revenue MTD"    curr={totalMtd}     prev={totalLast} prevLabel="last mo" />
        <MtdCard label="Parts MTD"            curr={d.mtd.parts}  prev={d.last_month.parts}  prevLabel="last mo" />
        <MtdCard label="Trailer MTD"          curr={d.mtd.trailer} prev={d.last_month.trailer} prevLabel="last mo" />
        <MtdCard label="Service MTD"          curr={d.mtd.service} prev={d.last_month.service} prevLabel="last mo" />
      </div>

      {/* YTD totals */}
      <section className="card p-4">
        <div className="section-eyebrow">Year to date ({d.period.year})</div>
        <div className="rule-crimson" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
          {[
            { label: 'Total YTD',    v: totalYtd },
            { label: 'Parts YTD',    v: d.ytd.parts },
            { label: 'Trailer YTD',  v: d.ytd.trailer },
            { label: 'Service YTD',  v: d.ytd.service },
          ].map(({ label, v }) => (
            <div key={label} className="text-center">
              <div className="text-xs text-muted uppercase tracking-wide">{label}</div>
              <div className="text-xl font-semibold text-navy tabular-nums">{fmt$(v)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* By branch */}
      {d.by_branch.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">By branch — {monthLabel}</div>
          <div className="rule-crimson" />
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted">
                <tr>
                  <th className="py-1">Branch</th>
                  <th className="text-right">Parts</th>
                  <th className="text-right">Trailer</th>
                  <th className="text-right">Service</th>
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {d.by_branch.map(b => (
                  <tr key={b.branch_name} className="border-t border-panelDark">
                    <td className="py-1 font-medium">{b.branch_name || '(unassigned)'}</td>
                    <td className="text-right tabular-nums">{fmt$(b.parts)}</td>
                    <td className="text-right tabular-nums">{fmt$(b.trailer)}</td>
                    <td className="text-right tabular-nums">{fmt$(b.service)}</td>
                    <td className="text-right tabular-nums font-semibold">
                      {fmt$(Number(b.parts) + Number(b.trailer) + Number(b.service))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Won this month */}
      {d.won_this_month.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Deals closed — {monthLabel}</div>
          <div className="rule-crimson" />
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted">
                <tr>
                  <th className="py-1">Rep</th>
                  <th className="text-right">Count</th>
                  <th className="text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {d.won_this_month.map(r => (
                  <tr key={r.rep || 'unknown'} className="border-t border-panelDark">
                    <td className="py-1">{r.rep || '(unassigned)'}</td>
                    <td className="text-right">{r.count}</td>
                    <td className="text-right tabular-nums font-medium">{fmt$(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Prior year same month comparison */}
      {totalPrYr > 0 && (
        <div className="text-xs text-muted text-right">
          {monthLabel.replace(String(d.period.year), String(d.period.year - 1))} total: {fmt$(totalPrYr)}
          {totalMtd > 0 && <> · YoY {fmtPct(pctChg(totalMtd, totalPrYr))}</>}
        </div>
      )}
    </div>
  );
}
